import React from 'react';
import styled from 'styled-components/macro';
import { useHistory } from 'react-router';
import { useOnClickOutside } from 'lib/utils';
import { Modal, PrimaryButton, RadioInput, TextInput } from 'lib/components';
import CloseIcon from 'lib/images/CloseIcon';
import { theme } from 'lib/style';
import { CustomReportItem } from 'lib/context/types';
import {
  addCustomReport,
  editCustomReport,
  useCustomReportsContext,
  useAuth /*** SUS-842 changes ***/,
} from 'lib/context';

import { Field } from 'lib/components/modal';
import {
  timeRangesOptions,
  timeRangeConstants,
} from './DateRangeSelector/DateRangeSelector';
import { ModalDateRangeSelector } from './DateRangeSelector/ModalDateRangeSelector';
import dayjs from 'dayjs';

const Header = styled.div`
  display: flex;
`;

const HeaderTitle = styled.div`
  ${theme.fontBold700}
  font-size: ${theme.fontSizes.lg};
  color: ${theme.palette.themeDark};
`;

const CloseButtonWrap = styled.div`
  margin-left: auto;
  color: ${theme.palette.white};
  height: 24px;
  width: 24px;
  &:hover {
    opacity: 0.7;
    cursor: pointer;
  }
`;

const Content = styled.div`
  width: 800px;
  display: flex;
  flex-direction: column;
  margin-top: 40px;
`;

const Label = styled.div`
  font-size: 14px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.43;
  letter-spacing: normal;
  color: #9297a2;
  margin-bottom: 8px;
`;

const RadioGroup = styled.div``;

const RadioLabel = styled.label`
  display: flex;
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: normal;
  color: ${theme.palette.black_1_100};
  cursor: pointer;
  height: 24px;
  margin-bottom: 24px;
  > span {
    margin-left: 12px;
    margin-top: 2px;
  }
`;

const Footer = styled.div`
  margin-top: 32px;
  display: flex;
  justify-content: flex-end;
  > button {
    margin-left: 12px;
  }
`;
/**** SUS-796 changes ***/
const ErrorInfo = styled.div`
  margin-bottom: 14px;
  ${theme.mediaQueryMaxWidth.md} {
    display: none;
  }
  span {
    color: rgb(232, 76, 61);
  }
`;

type Props = {
  reports: Field[];
  handleModalClose: (reportId?: string) => void;
  initReport?: {
    reportId?: string;
    title?: string;
    range?: string;
  };
  users?: string[];
  goToReportDetails?: boolean;
  onBackClick?: () => void;
  extraParams?: any;
};

export const CustomReportModal = (props: Props) => {
  const {
    reports,
    handleModalClose,
    initReport,
    goToReportDetails = true,
    users = [],
    onBackClick,
  } = props;
  const [title, setTitle] = React.useState(
    (initReport && initReport.title) || ''
  );
  const [timeRange, setTimeRange] = React.useState(
    (initReport && initReport.range) || timeRangesOptions[0].value
  );

  const [showRangeModal, setShowRangeModal] = React.useState(false);
  const [endDate, setEndDate] = React.useState(new Date());
  const [startDate, setStartDate] = React.useState(new Date());

  const shouldUpdateReport = !!initReport;

  const history = useHistory();
  const { ref } = useOnClickOutside(handleModalClose);

  const escPress = (key: string) => {
    if (key === 'Escape') {
      handleModalClose();
    }
  };
  React.useEffect(() => {
    document.addEventListener('keyup', e => escPress(e.key));

    return () =>
      document.removeEventListener('keyup', e => escPress(e.key), true);
  }, []);

  const { dispatch } = useCustomReportsContext();

  const { userData } = useAuth(); /*** SUS-842 changes ***/
  const loggedUserId: string[] = [userData.userId]; /*** SUS-842 changes ***/

  const startDateString = dayjs(startDate).format(
    startDate.getFullYear() === endDate.getFullYear()
      ? 'MMM DD'
      : 'MMM DD, YYYY'
  );
  const endDateString = dayjs(endDate).format('MMM DD, YYYY');

  const upsertReport = async () => {
    const data = {
      dispatch,
      data: {
        title,
        range: timeRange,
        startDate,
        endDate,
        reports: reports.map(report => report.value),
      },
      reportId: '',
    };

    if (users.length) {
      // @ts-ignore
      data.data.users = users;
    } else {
      // @ts-ignore
      data.data.users = loggedUserId; /*** SUS-842 changes ***/
    }

    let customReport = {} as CustomReportItem;
    if (initReport && initReport.reportId) {
      data.reportId = initReport.reportId;
      customReport = await editCustomReport(data);
    } else {
      customReport = await addCustomReport(data);
    }

    if (customReport && customReport.reportId) {
      handleModalClose(customReport.reportId);
      if (goToReportDetails) {
        history.push(`/reports/custom/${customReport.reportId}`);
      }
    }
  };

  const onCustomRangeSelected = (
    customStartDate: Date,
    customEndDate: Date
  ) => {
    customStartDate = customStartDate || new Date();
    customEndDate = customEndDate || new Date();
    setStartDate(customStartDate);
    setEndDate(customEndDate);
    setShowRangeModal(false);
  };

  const onDateRangeChange = (value: string) => {
    if (value === timeRangeConstants.CUSTOM) {
      setShowRangeModal(true);
    }
    setTimeRange(value);
  };

  return (
    <Modal style={{ position: 'fixed' }}>
      <div ref={ref} style={{ padding: '32px' }}>
        <Header>
          <HeaderTitle>
            {shouldUpdateReport ? 'Edit Custom Report' : 'New Custom Report'}
          </HeaderTitle>
          <CloseButtonWrap title={'Close'}>
            <CloseIcon
              onClick={() => handleModalClose()}
              width={24}
              height={24}
              color={theme.palette.label}
            />
          </CloseButtonWrap>
        </Header>
        <Content>
          <div>
            <Label>Report’s Name</Label>
            <TextInput
              value={title}
              onChange={event => setTitle(event.target.value)}
            />
            {/* SUS-796 changes */}
            {title && title.length > 45 && (
              <ErrorInfo>
                <span>
                  Report name can not contain more than 45 characters.
                </span>
              </ErrorInfo>
            )}
          </div>
          <div style={{ marginTop: '24px' }}>
            <Label>Default report date range</Label>
            <RadioGroup role='group' aria-labelledby='type-group'>
              {timeRangesOptions.map(range => (
                <RadioLabel key={range.value}>
                  <RadioInput
                    name='timeframe'
                    value={range.value}
                    checked={range.value === timeRange}
                    onClick={() => onDateRangeChange(range.value)}
                    onChange={() => onDateRangeChange(range.value)}
                  />
                  <span>
                    {range.label}{' '}
                    {timeRange === timeRangeConstants.CUSTOM &&
                      range.value === timeRangeConstants.CUSTOM &&
                      `${startDateString} - ${endDateString}`}
                  </span>
                </RadioLabel>
              ))}
            </RadioGroup>
          </div>
        </Content>
        <Footer>
          {onBackClick && (
            <PrimaryButton
              text='Back'
              backgroundColor={theme.palette.primaryDarkBlue10Percent}
              textColor={theme.palette.black_1_100}
              borderRadius={5}
              fontSize='15px'
              textPadding='8px 16px'
              onClick={() => onBackClick()}
            />
          )}

          <PrimaryButton
            text={shouldUpdateReport ? 'Edit Report' : 'Create Report'}
            textColor='white'
            borderRadius={5}
            fontSize='15px'
            textPadding='8px 16px'
            onClick={() => upsertReport()}
            disabled={!title.length || title.length > 45} /* SUS-796 changes */
          />
        </Footer>
        {showRangeModal && (
          <ModalDateRangeSelector
            initStartDate={startDate}
            initEndDate={endDate}
            minDate={new Date('01-01-2022')}
            handleModalClose={() => {
              setShowRangeModal(false);
            }}
            onRangeSelected={onCustomRangeSelected}
          />
        )}
      </div>
    </Modal>
  );
};
